/**
 * This file moves into the route tree on TanStack. Every part of it has a
 * direct equivalent there:
 *
 * - each row becomes `staticData: { backButton }` on its own route
 * - `$hub` and `$title` are path params already, spelled the same way
 * - a trailing `/$` is a splat child, which is why it matches strictly below
 * - an exact row for a landing page is that route's index route
 * - `compiledRoutes` and `matchesPath` go: the router does the matching, and
 *   `useMatches()` returns every match root-first
 * - the fold becomes `useMatches({ select })`, taking the last match that
 *   declares the field
 * - `stripLocalePrefix` goes too; the locale is a route segment there
 * - the overlap test goes: one path cannot match two routes in a tree
 */
import { stripLocalePrefix } from "@/utilities/locale";

const backButtonRoutes: Record<string, boolean> = {
  "/betting/match/$": true,
  "/betting/futures/$": true,
  "/$hub/betting/match/$": true,
  "/$hub/betting/futures/$": true,
  "/$hub/$title/betting/match/$": true,
  "/$hub/$title/betting/futures/$": true,
  "/racing/betting/race/$": true,
  "/racing/betting/meeting/$": true,
  "/casino/$": true,
  "/casino/lobby": false,
  "/pickems/contest": true,
  "/pickems/contest/$": true,
  "/$title/pickems/contest": true,
  "/$title/pickems/contest/$": true,
  "/account/$": true,
  "/account/reopen": false,
  "/entries/$": true,
  "/entries/casino": false,
  "/perks/$": true,
  "/perks/rakeback": false,
  "/preferences/$": true,
};

type CompiledRoute = {
  segments: string[];
  isPrefix: boolean;
  backButton: boolean;
};

const toSegments = (pathname: string) => pathname.split("/").filter(Boolean);

const compile = ([pattern, backButton]: [string, boolean]): CompiledRoute => {
  const isPrefix = pattern.endsWith("/$");

  return {
    segments: toSegments(isPrefix ? pattern.slice(0, -2) : pattern),
    isPrefix,
    backButton,
  };
};

const compiledRoutes = Object.entries(backButtonRoutes)
  .map(compile)
  .sort((a, b) => a.segments.length - b.segments.length);

const matchesPath = (route: CompiledRoute, segments: string[]) => {
  const depthFits = route.isPrefix
    ? segments.length > route.segments.length
    : segments.length === route.segments.length;

  return (
    depthFits &&
    route.segments.every(
      (segment, index) =>
        segment.startsWith("$") || segment === segments[index],
    )
  );
};

const resolveBackButton = (pathname: string): boolean => {
  const segments = toSegments(stripLocalePrefix(pathname));

  return compiledRoutes.reduce(
    (backButton, route) =>
      matchesPath(route, segments) ? route.backButton : backButton,
    false,
  );
};

export { backButtonRoutes, resolveBackButton };
